import { useEffect, useState } from "react";
import FormTemplate from "../../templates/FormTemplate";
import {
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { PrintService } from "../../../services/api";
import { useTranslation } from "react-i18next";
import { TicketPrintTemplate } from "../../../services/swagger";
import { useNavigate } from "react-router";
import FieldLabel from "../../atoms/FieldLabel";
import DeleteButton from "../../atoms/DeleteButton";
import useConfirm from "../../../hooks/useConfirm";
import { useMutation, useMutationState } from "@tanstack/react-query";
import LoadingTextLine from "../../atoms/LoadingTextLine";
import useBreakpoint from "../../../hooks/useBreakpoint";

export default function PrintTemplateSettingsPage() {
  const breakpoint = useBreakpoint();
  const { t } = useTranslation("Settings");
  const navigate = useNavigate();
  const confirm = useConfirm();

  const handleTicketPrintTemplateClick = () => {
    navigate("/m/settings/ticket-print-template/create");
  };

  const [loading] = useMutationState({
    filters: { mutationKey: ["settings"] },
    select: (mutation) => mutation.state.status === "pending",
  });

  const [ticketPrintTemplates, setTicketPrintTemplates] = useState<
    TicketPrintTemplate[]
  >([]);

  const {
    mutate: loadTicketPrintTemplates,
    isPending: ticketPrintTemplatesLoading,
  } = useMutation({
    mutationFn: async () => {
      const res = await PrintService.getTicketPrintTemplateList();
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) setTicketPrintTemplates(data);
    },
  });

  const handleDeleteTicketPrintTemplate = (_id?: string) => {
    if (!_id) return;
    confirm(async () => {
      const res = await PrintService.deleteTicketPrintTemplate({ id: _id });
      if (res?.data?.success) loadTicketPrintTemplates();
    });
  };

  useEffect(() => {
    loadTicketPrintTemplates();
  }, []);

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <FormTemplate
      title={t("PrintTemplates")}
      disableFooter
      loading={loading}
      onCancel={handleClose}
    >
      <Grid container rowSpacing={2}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Stack
            direction={"row"}
            sx={{ alignItems: "center", mb: 1 }}
            spacing={"auto"}
          >
            <FieldLabel>{t("AvailableTemplates")}</FieldLabel>
            <Button
              startIcon={<AddIcon />}
              // size="small"
              sx={{ width: "fit-content" }}
              variant="outlined"
              // onClick={() => navigate("/m/settings/ticket-print-template")}
              onClick={handleTicketPrintTemplateClick}
            >
              {t("AddTemplate")}
            </Button>
          </Stack>
          <Stack flexGrow={1} overflow="auto" spacing={2}>
            {ticketPrintTemplatesLoading ? (
              <LoadingTextLine />
            ) : ticketPrintTemplates.length === 0 ? (
              <Typography color="text.secondary">
                {t("NoPrintTemplateFound")}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="medium">
                  <TableBody>
                    {ticketPrintTemplates.map((template) => (
                      <TableRow
                        key={template._id}
                        hover
                        sx={{
                          width: 1,
                          p: 1,
                          bgcolor: "background.default",
                          cursor: "pointer",
                        }}
                      >
                        <TableCell
                          onClick={() =>
                            navigate(
                              `/m/settings/ticket-print-template/${template._id}`
                            )
                          }
                        >
                          {template.name}
                        </TableCell>
                        <TableCell width={2}>
                          <DeleteButton
                            onClick={() =>
                              handleDeleteTicketPrintTemplate(template._id)
                            }
                            size={25}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </Grid>
      </Grid>
    </FormTemplate>
  );
}
